import * as helpers from "./helper";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { SystemProgram, SYSVAR_RENT_PUBKEY, Transaction} from "@solana/web3.js";
import {Buffer} from "buffer";
import { Program, web3, BN } from '@coral-xyz/anchor';
import {hasValue} from "./helperFunctions";
import {getEnvironment, getIdoProgramName} from "./helper";
import * as CONSTANTS from "../constants/constants";
import idoIdl from "../web3/idl/star_ido.json"
import {ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID} from "@solana/spl-token";
import {PROD_USDC_MINT_ADDRESS} from "../constants/constants";
import {findUserPDA, findPoolPDA, findTokenVaultPDA, findUsdcVaultPDA, findStarVaultPDA} from "./utils";

const anchor = require('@coral-xyz/anchor');
const { PublicKey } = anchor.web3;

export const isSaleOngoing = (idoInfo, currentStage) => {
  if (!idoInfo) return false

  const curTime = moment().unix();

  if (!idoInfo.isNodeSale) {
    if (!idoInfo.salesStartTime || !idoInfo.salesEndTime) return false;

    const hydrazineStart = idoInfo.registrationStartTime;
    const openSaleEndNum = hasValue(idoInfo?.openSaleEndTime) ? idoInfo.openSaleEndTime : idoInfo?.salesEndTime;

    const hasSalestarted =  curTime > hydrazineStart;
    const hasSaleEnded = curTime > openSaleEndNum;

    return hasSalestarted && !hasSaleEnded;
  }

  const saleStartNum = getNodeSaleStartTimeUnix(idoInfo);
  const saleEndNum = getNodeSaleEndUnix(idoInfo);

  const hasSaleStartedNode =  curTime > saleStartNum;
  const hasSaleEndedNode = curTime > saleEndNum;

  return hasSaleStartedNode && !hasSaleEndedNode;
}

export const getNodeSaleStartTimeUnix = (idoInfo) => {
  if (!hasValue(idoInfo)) return 0;

  const nodeTiers = idoInfo?.nodeTiers;
  const ogTier = nodeTiers?.ogTier;
  const stakerTier = nodeTiers?.stakerTier;
  const fcfsTier = nodeTiers?.fcfsTier;

  if (ogTier?.active) {
    return ogTier.startTime;
  }
  if (stakerTier?.active) {
    return stakerTier.startTime;
  }
  if (fcfsTier?.active) {
    return fcfsTier.startTime;
  }
}

export const getNodeSaleEndUnix = (idoInfo) => {
  if (!hasValue(idoInfo)) return 0;

  const nodeTiers = idoInfo?.nodeTiers;
  const ogTier = nodeTiers?.ogTier;
  const stakerTier = nodeTiers?.stakerTier;
  const fcfsTier = nodeTiers?.fcfsTier;

  if (fcfsTier?.active) {
    return fcfsTier.endTime;
  }
  if (stakerTier?.active) {
    return stakerTier.endTime;
  }
  if (ogTier?.active) {
    return ogTier.endTime;
  }
}

export const PROGRAM_NAME = 'ido_vault';
export const PROD_NEW_IDO_PROGRAM_NAME = "star_ido_new";
export const PROD_IDO_PROGRAM_ADDREESS = "7im9orzthW5w58Thf3d7RjhPCPpqGMGhgtZ11tQfgLxp";
export const DEV_IDO_PROGRAM_ADDREESS = "9zZJzETveeNmW8s7EHqSnMb6TJyofNpvbCUShQpiomVy";
export const PROD_NEW_IDO_PROGRAM_ADDRESS = "AiLDjhABGJjSvgAkd5wyPKCsuEGQ9RK2PnpixS6A68G1";

export const getIdoProgram = (provider, useNewStarsContract = true) => {
  const idl = idoIdl;

  return new anchor.Program(idl, provider);
}

export const getIdoProgramAddress = () => {
  return getEnvironment() === CONSTANTS.PRODUCTION ? PROD_NEW_IDO_PROGRAM_ADDRESS : PROD_NEW_IDO_PROGRAM_ADDRESS;
}

export const getNodeIdoProgramName = () => {
  return getEnvironment() === CONSTANTS.PRODUCTION ? CONSTANTS.PROD_NODE_IDO_PROGRAM_NAME : CONSTANTS.DEV_NODE_IDO_PROGRAM_NAME;
}

export const getIdoProgramId =  () => {
  return new web3.PublicKey(getIdoProgramAddress());
}

export const getIdoProgramNameBuffer = () => {
  return Buffer.from(getIdoProgramName());
}

export const getNodeIdoProgramNameBuffer = () => {
  return Buffer.from(getNodeIdoProgramName());
}

export const getPoolUserBuffer = () => {
  const seed = getEnvironment() === CONSTANTS.PRODUCTION ? CONSTANTS.PROD_POOL_USER_SEED : CONSTANTS.DEV_POOL_USER_SEED;
  return Buffer.from(seed);
}

export const checkIfIdoUser = async ({ provider, connection }) => {
  const programId = getIdoProgramId();

  const [idoAcct, idoAcctBump] = await PublicKey.findProgramAddress(
      [
        provider.wallet.publicKey.toBuffer(),
        getIdoProgramNameBuffer(),
        getPoolUserBuffer()
      ],
      programId
  );

  const idoAccountInit = await provider.connection.getBalance(idoAcct);

  return idoAccountInit > 0;
}


export const createIdoAccount = async (provider) => {
    const programId = getIdoProgramId();
    const idoProgram = getIdoProgram(provider);
    const user = findUserPDA(provider.wallet.publicKey, programId);

    const signature = await idoProgram.methods.createUser().accounts({
      authority: provider.wallet.publicKey,
      user: user,
      systemProgram: SystemProgram.programId,
      rent: SYSVAR_RENT_PUBKEY,
    }).rpc();

    await provider.connection.confirmTransaction(signature, 'processed');
    toast("IDO Account Created", successToast);
 }

export const claimUSDC = async ({ provider, connection, poolName }) => {

  const programId = getIdoProgramId();
  const programName = getIdoProgramName();
  const idoProgram = getIdoProgram(provider);

  // Derive program addresses
  const [mainData] = PublicKey.findProgramAddressSync(
      [Buffer.from(programName)],
      programId
  );

  const [pool] = PublicKey.findProgramAddressSync(
      [poolName],
      programId
  );

  const usdcMintKey = new PublicKey(PROD_USDC_MINT_ADDRESS);
  const [managerUsdc] = PublicKey.findProgramAddressSync(
      [
        provider.wallet.publicKey.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        usdcMintKey.toBuffer()
      ],
      ASSOCIATED_TOKEN_PROGRAM_ID
  );

  const [usdcVault] = PublicKey.findProgramAddressSync(
      [Buffer.from(poolName), Buffer.from("usdc_vault")],
      programId
  );

  console.log("program", idoProgram);

  try {

    // modify to use wallet.publicKey
    const tx = await idoProgram.methods
        .endSale()
        .accounts({
          owner: provider.wallet.publicKey,
          pool,
          mainData,
          usdcMint: usdcMintKey,
          managerUsdc,
          usdcVault,
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID
        }).rpc();

    return tx;

  } catch (error) {
    console.error('Failed to end sale:', error);
    throw error;
  }
}

export const createIdo = async ({ provider, idoData }) => {
  const {
    idoName
  } = idoData;

  const programId = getIdoProgramId();
  const programName = getIdoProgramName();
  const idoProgram = getIdoProgram(provider);

  const [mainData, mainDataBump] = await PublicKey.findProgramAddress(
      [Buffer.from(programName)],
      programId
  );

  const pool = findPoolPDA(programId, idoName);
  const tokenVault = findTokenVaultPDA(programId, idoName);
  const usdcVault = findUsdcVaultPDA(programId, idoName);
  const starVault = findStarVaultPDA(programId, idoName);

  let usdcMintKey = new PublicKey("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v");
  let starsMintKey = new PublicKey("HCgybxq5Upy8Mccihrp7EsmwwFqYZtrHrsmsKwtGXLgW");
  let tokenMintKey = new PublicKey("6bHmA5Jof95m3V4oekGaaxjqkmkneuBDuD1EZDXpGKnh");

  let maxStars = new BN(Number(idoData.maxStars) * 1000000); // Where are the Hydrazines? maxStars*10^6
  let individualLimit = new BN(Number(idoData.individualLimit) * 1000000); // 5% of the maxStars
  let tokensForSale = new BN(Number(idoData.tokensForSale) * 1000000); //2,500,000 0000 Tokens*10^6
  let usdcAmount = new BN(Number(idoData.usdcAmount) * 1000000); // TotalAmount * 10^6
  let price = new BN(Number(idoData.price) * 1000000); // Price in USDC*10^6
  let manager = provider.wallet.publicKey;
  const idoTimes = {
    registrationStart: new BN(idoData.registrationStart),
    registrationEnd: new BN(idoData.registrationEnd),
    saleStart: new BN(idoData.saleStart),
    saleEnd: new BN(idoData.saleEnd),
    openSaleStart: new BN(idoData.openSaleStart),
    vestingStart: new BN(3505891646) // or use idoData.vestingStart if that's desired
  };

  let openMaxAlloc = tokensForSale.div(new BN(Number(idoData.openMaxAlloc) * 1000000)); // Can be updated on from updateFCFS. Calculation based on tokens

  //let openMaxAllocPer = new BN(Number(idoData.openMaxAllocPer));
  let vested = true;
  let unlocked =  25; // Initial Unlock Percentage
  let tranches = new BN(3);
  let trancheLength = new BN(2592000); // in Second

  console.log(idoName)

  const signature =  await idoProgram.methods.createPool(
      idoName,
      maxStars,
      individualLimit,
      tokensForSale,
      usdcAmount,
      price,
      manager,
      idoTimes,
      openMaxAlloc,
      vested,
      unlocked,
      tranches,
      trancheLength,
    ).accounts( {
        programAuth: provider.wallet.publicKey,
        mainData: mainData,
        pool: pool,
        starMint: starsMintKey,
        tokenMint: tokenMintKey,
        usdcMint: usdcMintKey,
        hydrazineVault: starVault,
        tokenVault: tokenVault,
        usdcVault: usdcVault,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
        rent: SYSVAR_RENT_PUBKEY,
  }).rpc()

  return await provider.connection.confirmTransaction(signature, 'processed');
}

export const updatePoolTimes = async ({ provider, idoData }) => {
  const {
    idoName
  } = idoData;
  console.log(idoData)
  const programId = getIdoProgramId();
  const idoProgram = getIdoProgram(provider);

  const [pool, bump] = await PublicKey.findProgramAddress(
      [Buffer.from(idoName)],
      programId
  );

  const idoTimes = {
    registrationStart: new BN(idoData.registrationStart),
    registrationEnd: new BN(idoData.registrationEnd),
    saleStart: new BN(idoData.saleStart),
    saleEnd: new BN(idoData.saleEnd),
    openSaleStart: new BN(idoData.openSaleStart),
    vestingStart: new BN(3505891646) // or use idoData.vestingStart if that's desired
  };

  let tokensForSale = new BN(Number(idoData.tokensForSale) * 1000000);
  let openMaxAlloc = tokensForSale.div(new BN(Number(idoData.openMaxAlloc) * 1000000)); // Can be updated on from updateFCFS. Calculation based on tokens

  // let vested = true;
  let unlocked =  25; // Initial Unlock Percentage
  let tranches = new BN(3);
  let trancheLength = new BN(2592000); // in Second

  const updateParams = {
    maxStars: idoData.maxStars ? new BN(Number(idoData.maxStars) * 1000000) : null,
    individualLimit: idoData.individualLimit ? new BN(Number(idoData.individualLimit) * 1000000) : null,
    tokensForSale: idoData.tokensForSale ? new BN(Number(idoData.tokensForSale) * 1000000) : null,
    usdcAmount: idoData.usdcAmount ? new BN(Number(idoData.usdcAmount) * 1000000) : null,
    price: idoData.price ? new BN(Number(idoData.price) * 1000000) : null,
    manager: provider.wallet.publicKey,
    openMaxAlloc: idoData.openMaxAlloc ? openMaxAlloc : null,
    vested: true,
    unlocked: unlocked,
    tranches: tranches,
    trancheLength: trancheLength,
    idoTimes,
  }

  const signature =  await idoProgram.methods.updatePoolParams(
      updateParams,
  ).accounts( {
    authority: provider.wallet.publicKey,
    pool: pool,
    systemProgram: SystemProgram.programId,
  }).rpc()

  return await provider.connection.confirmTransaction(signature, 'processed');
}

export const whitelistMerkleRootToPool = async ({ provider, poolRoot, poolName }) => {
  const programId = getIdoProgramId();
  const idoProgram = getIdoProgram(provider);

  const pool = findPoolPDA(programId, poolName);

  const signature = await idoProgram.methods.whitelistPool(
      poolRoot
  ).accounts({
    managerAuthority: provider.wallet.publicKey,
    pool: pool,
    systemProgram: anchor.web3.SystemProgram.programId,
  }).rpc();

  return await provider.connection.confirmTransaction(signature, 'processed');
}


export const DEFAULT_IDO_COUNTRIES =  [
  "AX",
  "AL",
  "DZ",
  "AS",
  "AD",
  "AO",
  "AI",
  "AQ",
  "AG",
  "AR",
  "AM",
  "AW",
  "AU",
  "AT",
  "AZ",
  "BS",
  "BH",
  "BD",
  "BB",
  "BY",
  "BE",
  "BZ",
  "BJ",
  "BM",
  "BT",
  "BO",
  "BQ",
  "BA",
  "BW",
  "BV",
  "BR",
  "IO",
  "BN",
  "BG",
  "BF",
  "BI",
  "CV",
  "KH",
  "CM",
  "KY",
  "TD",
  "CL",
  "CX",
  "CC",
  "CO",
  "KM",
  "CK",
  "CR",
  "CI",
  "HR",
  "CW",
  "CY",
  "CZ",
  "DK",
  "DJ",
  "DM",
  "DO",
  "EC",
  "EG",
  "SV",
  "GQ",
  "EE",
  "SZ",
  "ET",
  "FK",
  "FO",
  "FJ",
  "FI",
  "FR",
  "GF",
  "PF",
  "TF",
  "GA",
  "GM",
  "GE",
  "DE",
  "GH",
  "GI",
  "GR",
  "GL",
  "GD",
  "GP",
  "GU",
  "GT",
  "GG",
  "GN",
  "GY",
  "HT",
  "HM",
  "VA",
  "HN",
  "HK",
  "HU",
  "IS",
  "IN",
  "ID",
  "IE",
  "IM",
  "IL",
  "IT",
  "JM",
  "JP",
  "JE",
  "JO",
  "KZ",
  "KE",
  "KI",
  "KR",
  "KW",
  "KG",
  "LA",
  "LV",
  "LS",
  "LR",
  "LI",
  "LT",
  "LU",
  "MO",
  "MK",
  "MG",
  "MW",
  "MY",
  "MV",
  "MT",
  "MH",
  "MQ",
  "MR",
  "MU",
  "YT",
  "MX",
  "FM",
  "MD",
  "MC",
  "MN",
  "ME",
  "MS",
  "MA",
  "MZ",
  "MM",
  "NR",
  "NP",
  "NL",
  "NC",
  "NZ",
  "NI",
  "NE",
  "NG",
  "NU",
  "NF",
  "MP",
  "NO",
  "OM",
  "PK",
  "PW",
  "PS",
  "PA",
  "PG",
  "PY",
  "PE",
  "PH",
  "PN",
  "PL",
  "PT",
  "QA",
  "RE",
  "RO",
  "RU",
  "RW",
  "BL",
  "SH",
  "KN",
  "LC",
  "MF",
  "PM",
  "VC",
  "WS",
  "SM",
  "ST",
  "SA",
  "SN",
  "RS",
  "SC",
  "SL",
  "SG",
  "SX",
  "SK",
  "SI",
  "SB",
  "ZA",
  "GS",
  "ES",
  "LK",
  "SR",
  "SJ",
  "SE",
  "CH",
  "TW",
  "TZ",
  "TH",
  "TL",
  "TG",
  "TK",
  "TO",
  "TT",
  "TN",
  "TR",
  "TM",
  "TC",
  "TV",
  "UG",
  "UA",
  "AE",
  "GB",
  "UM",
  "UY",
  "UZ",
  "VU",
  "VN",
  "VG",
  "WF",
  "EH",
  "ZM",
  "ZW"
]